import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Server, Globe, Shield, BarChart, Code, Database } from 'lucide-react';

const Solutions = () => {
  return (
    <div className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="text-white">Technology </span>
            <span className="text-neonGreen">Solutions</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Powerful tools for small businesses of all kinds. Start with what you want to improve, and we’ll help you find a solution that fits your budget.
          </p>
        </motion.div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <SolutionCard
            title="Everyday Automation"
            icon={<Server size={32} />}
            description="Reduce repetitive admin with automated reminders, customer follow-ups, and simple workflows."
          />
          <SolutionCard
            title="Websites That Work"
            icon={<Globe size={32} />}
            description="Give customers a clear, mobile-friendly place to learn about your business, book a service, or get in touch."
          />
          <SolutionCard
            title="Connected Tools"
            icon={<Shield size={32} />}
            description="Help your existing apps share information so you spend less time copying details between systems."
          />
          <SolutionCard
            title="Clearer Business Insights"
            icon={<BarChart size={32} />}
            description="Bring scattered spreadsheets and reports together so you can see what’s happening and make informed decisions."
          />
          <SolutionCard
            title="Custom Software"
            icon={<Code size={32} />}
            description="From intake forms to internal tools, build something around your process when off-the-shelf software falls short."
          />
          <SolutionCard
            title="Practical AI"
            icon={<Database size={32} />}
            description="Use AI to draft content, organize information, or assist with routine questions, with human review where it matters."
          />
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center bg-gradient-to-br from-darkGrey to-darkPurple p-10 rounded-2xl border border-deepPurple/30"
        >
          <h2 className="text-3xl font-bold mb-4 text-white">What would make your workday easier?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            You don’t need a big technology plan to get started. Tell us about your business, what’s slowing you down, and what you’d like to spend.
          </p>
          <Link to="/contact" className="inline-flex items-center px-8 py-3 rounded-full bg-gradient-to-r from-neonGreen to-electricBlue text-darkGrey font-semibold text-lg shadow-lg shadow-electricBlue/20">
            Let’s talk
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

// Solution Card Component
const SolutionCard = ({ title, icon, description }) => {
  return (
    <motion.div
      whileHover={{ y: -10 }}
      className="bg-gradient-to-br from-darkGrey to-darkPurple p-6 rounded-xl border border-deepPurple/30 shadow-xl"
    >
      <div className="text-electricBlue mb-4">{icon}</div>
      <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </motion.div>
  );
};

export default Solutions;
